// packages/extension/components/OnboardingPage.tsx
import React, { useCallback } from 'react';
import { TimestampIcon, ChevronLeftIcon } from "../components/Icons";
import TublyLogo from "../components/TublyLogo";
import { CHROME_STORE_URL } from '../constants';

/* =========================================================================
   Shared: topic-aware photos (Unsplash crops, 16:9 @ 1280×720)
   - You can later replace these URLs with local paths (/img/photos/*.jpg)
   - Deterministic picker keeps visuals stable between renders
   ========================================================================= */

const PHOTOS = {
  crypto: [
    'https://unsplash.com/photos/VM_6EtTAfDQ/download?force=true&w=1280&h=720&fit=crop',
    'https://unsplash.com/photos/H1jdfM5jUxM/download?force=true&w=1280&h=720&fit=crop',
  ],
  psych: [
    'https://unsplash.com/photos/rNSQ-EuBi6o/download?force=true&w=1280&h=720&fit=crop',
    'https://unsplash.com/photos/tw-mAZXr6H4/download?force=true&w=1280&h=720&fit=crop',
  ],
  mma: [
    'https://unsplash.com/photos/9RF7fK7ujFs/download?force=true&w=1280&h=720&fit=crop',
    'https://unsplash.com/photos/jHUckCgZSGc/download?force=true&w=1280&h=720&fit=crop',
  ],
  english: [
    'https://unsplash.com/photos/c6QLJhezaYs/download?force=true&w=1280&h=720&fit=crop',
    'https://unsplash.com/photos/Y8TiLvKnLeg/download?force=true&w=1280&h=720&fit=crop',
  ],
  generic: [
    'https://unsplash.com/photos/mV8vk4Ds3r8/download?force=true&w=1280&h=720&fit=crop',
    'https://unsplash.com/photos/MA1Xobltz5o/download?force=true&w=1280&h=720&fit=crop',
  ],
} as const;

const TOPIC_MAP: Array<{ keys: RegExp[]; pool: readonly string[] }> = [
  { keys: [/crypto|bitcoin|ethereum|defi|fomc|markets?|trading/i], pool: PHOTOS.crypto },
  { keys: [/schizo|psych|therapy|neuro|mental|brain|trauma/i],     pool: PHOTOS.psych },
  { keys: [/mma|ufc|fight|boxing|kick|jiu[-\s]?jitsu|wrestling/i],  pool: PHOTOS.mma },
  { keys: [/english|language|vocab|ielts|toefl|learn/i],            pool: PHOTOS.english },
];

function hash32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
  }
  return h >>> 0;
}

function pickPhotoByTopic(title: string, channel: string, seed = 0): string {
  const text = `${title} ${channel}`;
  for (const bucket of TOPIC_MAP) {
    if (bucket.keys.some(rx => rx.test(text))) {
      const i = Math.abs(hash32(text + seed)) % bucket.pool.length;
      return bucket.pool[i];
    }
  }
  const g = PHOTOS.generic;
  return g[Math.abs(hash32(text + seed)) % g.length];
}

/* =========================================================================
   Shared: topic-aware avatars (Unsplash face crops, 96×96) + initials fallback
   ========================================================================= */

const AVATARS: Record<string, string[]> = {
  'Psych Talk': [
    'https://images.unsplash.com/photo-1554151228-14d9def656e4?q=80&w=96&h=96&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?q=80&w=96&h=96&fit=crop&crop=faces',
  ],
  'MMA Breakdown': [
    'https://images.unsplash.com/photo-1598970434795-0c54fe7c0642?q=80&w=96&h=96&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?q=80&w=96&h=96&fit=crop&crop=faces',
  ],
  'English Lab': [
    'https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=96&h=96&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?q=80&w=96&h=96&fit=crop&crop=faces',
  ],
  // Optional: give "Lab Of Crypto" a photoreal avatar; otherwise it will fall back to initials.
  'Lab Of Crypto': [
    'https://images.unsplash.com/photo-1527980965255-d3b416303d12?q=80&w=96&h=96&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1544723795-3fb6469f5b39?q=80&w=96&h=96&fit=crop&crop=faces',
  ],
};

function pickAvatarForChannel(channel: string): string | null {
  const pool = AVATARS[channel];
  if (!pool?.length) return null;
  const i = hash32(channel) % pool.length;
  return pool[i];
}

function initialsAvatarDataUrl(name: string): string {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(s => s[0]?.toUpperCase() ?? '')
    .join('');
  const hue = hash32(name) % 360;
  const bg1 = `hsl(${hue},85%,60%)`;
  const bg2 = `hsl(${(hue + 25) % 360},85%,50%)`;
  const svg = encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 96 96' role='img' aria-label='${name}'>
      <defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>
        <stop offset='0%' stop-color='${bg1}'/><stop offset='100%' stop-color='${bg2}'/>
      </linearGradient></defs>
      <rect width='96' height='96' rx='24' fill='url(#g)'/>
      <text x='50%' y='54%' font-family='ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto' font-size='36' font-weight='700' text-anchor='middle' fill='white'>${initials}</text>
    </svg>`
  );
  return `data:image/svg+xml;charset=utf-8,${svg}`;
}

function getChannelAvatar(channel: string): string {
  return pickAvatarForChannel(channel) ?? initialsAvatarDataUrl(channel);
}

/* =========================================================================
   Local icons from file
   ========================================================================= */

const PuzzlePieceIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h3m-3 3h3m-3 3h3m-3 3h3M3 3h18v18H3V3z" />
  </svg>
);

const PinIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

/* =========================================================================
   Mock items with photo/avatar pickers
   ========================================================================= */

const MockChannelItem: React.FC<{name: string, thumb: string, count: number, isSelected: boolean}> = ({ name, thumb, count, isSelected }) => (
  <div className="flex flex-col items-center space-y-1 w-16">
    <div className="relative">
      <img src={thumb} alt={name} className={`w-12 h-12 rounded-full border-2 p-px ${isSelected ? 'border-red-500' : 'border-gray-300'}`} />
      {count > 0 && <span className="absolute top-0 right-0 block h-4 w-4 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{count}</span>}
    </div>
    <p className={`text-xs font-medium truncate w-full text-center ${isSelected ? 'text-gray-800' : 'text-gray-500'}`}>{name}</p>
  </div>
);

// thumb made optional; falls back to topic photo
const MockVideoItem: React.FC<{title: string, channel: string, thumb?: string}> = ({ title, channel, thumb }) => {
  const src = thumb ?? pickPhotoByTopic(title, channel, 1);
  return (
    <div className="flex flex-col group bg-white rounded-lg shadow-sm overflow-hidden text-left w-full">
      <div className="w-full aspect-video relative bg-gray-200">
        <img src={src} alt={title} className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="p-2">
        <h3 className="text-xs font-semibold text-gray-800 line-clamp-2">{title}</h3>
        <p className="text-[10px] text-gray-500 mt-1">{channel}</p>
      </div>
    </div>
  );
};

const MockPopupView: React.FC = () => (
  <div className="w-[380px] h-[550px] bg-gray-50 rounded-2xl shadow-2xl ring-1 ring-gray-900/10 overflow-hidden flex flex-col transition-shadow duration-300 hover:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.3)]">
    <header className="flex items-center justify-center p-3 border-b border-gray-200 bg-white flex-shrink-0">
      <div className="flex items-center gap-2">
        <TublyLogo className="w-7 h-auto" />
        <h1 className="text-lg font-bold text-gray-900">tubly</h1>
      </div>
    </header>
    <div className="p-4 space-y-4 overflow-y-auto scrollbar-hide">
      <div>
        <h2 className="text-sm font-semibold text-gray-500 mb-3">Today's Answers</h2>
        <div className="text-xs text-gray-500 p-4 bg-gray-100 rounded-lg">A carousel of AI-summarized videos would appear here.</div>
      </div>
      <div>
        <h2 className="text-sm font-semibold text-gray-500 mb-3">Your Channels</h2>
        <div className="flex space-x-3">
          <MockChannelItem name="Psych Talk"    thumb={getChannelAvatar('Psych Talk')}    count={5} isSelected={true} />
          <MockChannelItem name="MMA Breakdown"  thumb={getChannelAvatar('MMA Breakdown')} count={3} isSelected={false} />
          <MockChannelItem name="English Lab"    thumb={getChannelAvatar('English Lab')}    count={1} isSelected={false} />
        </div>
      </div>
      <div>
        <h2 className="text-sm font-semibold text-gray-500 mb-3">New Releases</h2>
        <div className="grid grid-cols-2 gap-3">
          <MockVideoItem title="Schizophrenia Explained in 10 Key Ideas" channel="Psych Talk" />
          <MockVideoItem title="UFC Game Plan: 5 Momentum Swings" channel="MMA Breakdown" />
        </div>
      </div>
    </div>
  </div>
);

const MockVideoDetailView: React.FC = () => {
  const title = 'Schizophrenia Explained in 10 Key Ideas';
  const channel = 'Psych Talk';
  const thumb = pickPhotoByTopic(title, channel, 2);
  return (
    <div className="w-[380px] h-[550px] bg-gray-50 rounded-2xl shadow-2xl ring-1 ring-gray-900/10 overflow-hidden flex flex-col transition-shadow duration-300 hover:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.3)]">
      <div className="p-4 space-y-3 overflow-y-auto scrollbar-hide">
        <div className="flex items-center text-sm font-semibold text-gray-600 cursor-pointer">
          <ChevronLeftIcon className="w-5 h-5 mr-1" /> Back
        </div>
        <div className="w-full aspect-video relative rounded-lg overflow-hidden shadow-md bg-gray-300">
          <img src={thumb} alt="Video thumbnail" className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div>
          <h1 className="text-base font-bold text-gray-900 leading-tight">{title}</h1>
          <p className="text-xs text-gray-500 mt-1">102K views • 2 days ago</p>
        </div>
        <div className="border-t border-gray-200 pt-3">
          <h2 className="text-sm font-semibold text-gray-800 mb-1">Direct Answer (AI)</h2>
          <p className="text-xs text-gray-600 leading-relaxed">Sums up the ten pillars behind schizophrenia—positive and negative symptoms, the dopamine-glutamate imbalance, and why early intervention with family routines keeps relapses at bay.</p>
        </div>
        <div className="border-t border-gray-200 pt-3">
          <h2 className="text-sm font-semibold text-gray-800 mb-2">Key Moments</h2>
          <div className="space-y-1">
            <div className="flex items-start gap-2 p-1.5 rounded-lg hover:bg-gray-200 cursor-pointer">
              <TimestampIcon className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-xs text-red-600">0:45</p>
                <p className="text-xs text-gray-600">Introduction to side effects.</p>
              </div>
            </div>
            <div className="flex items-start gap-2 p-1.5 rounded-lg hover:bg-gray-200 cursor-pointer">
              <TimestampIcon className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-xs text-red-600">2:15</p>
                <p className="text-xs text-gray-600">Deep dive into symptoms.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MockCopilotView: React.FC = () => (
    <div className="w-full max-w-4xl h-[500px] bg-[#0f0f0f] text-white font-[Roboto,Arial,sans-serif] flex overflow-hidden rounded-2xl shadow-2xl ring-1 ring-gray-900/10 transition-shadow duration-300 hover:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.3)]">
        {/* Left Side: Video Player and Info */}
        <div className="flex-1 flex flex-col">
            <div className="w-full aspect-video bg-black flex items-center justify-center overflow-hidden">
                <img src="https://loremflickr.com/640/360/code,react?lock=309" alt="Video player thumbnail" className="w-full h-full object-cover" />
            </div>
            <div className="p-4">
                <h2 className="text-lg font-bold leading-tight line-clamp-2">Schizophrenia Explained in 10 Key Ideas</h2>
                <div className="flex items-center mt-3">
                    <img className="w-10 h-10 rounded-full" src="https://loremflickr.com/40/40/abstract?lock=302" alt="Channel avatar" />
                    <div className="ml-3">
                      <p className="font-semibold text-sm">Psych Talk</p>
                      <p className="text-xs text-gray-400">102K views</p>
                    </div>
                </div>
            </div>
        </div>
        
        {/* Right Side: Copilot Dock */}
        <div className="w-[340px] flex-shrink-0 bg-[#1a1a1a] border-l border-gray-700/50 flex flex-col p-3 gap-3">
            <header className="flex justify-between items-center">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-200">
                    <TublyLogo className="w-5 h-auto" />
                    Tubly Copilot
                </div>
                <div className="flex text-xs bg-black/50 p-0.5 rounded-full">
                    <button className="px-2.5 py-0.5 font-medium text-black bg-gray-200 rounded-full">This video</button>
                </div>
            </header>
            <div className="flex-1 flex flex-col gap-3 overflow-y-auto text-sm">
                {/* Assistant Message */}
                <div className="flex items-start gap-2 justify-start">
                  <div className="max-w-[85%] rounded-xl px-3 py-2 bg-red-500 text-white rounded-br-lg">
                        what does Dr. Alves say to ground yourself when the voices spike?
                    </div>
                    <div className="w-6 h-6 flex-shrink-0 rounded-full bg-gray-700 flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" /></svg>
                    </div>
                </div>
                {/* User Message */}
                <div className="flex items-start gap-2 justify-end">
                    <div className="w-6 h-6 flex-shrink-0 rounded-full bg-gray-700 flex items-center justify-center">
                        <TublyLogo className="w-3 h-auto" />
                    </div>
                    <div className="max-w-[85%] rounded-xl px-3 py-2 bg-gray-700 text-gray-200 rounded-bl-lg">
                        He walks through the three-step routine—slow breathing, naming five nearby objects, and texting your support buddy—
                        <button className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs font-medium bg-white/10 text-gray-300 hover:bg-white/20 mx-1">
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 text-red-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" /></svg>
                          <span>@2:15</span>
                        </button>
                        to keep the mind anchored during symptom spikes.
                    </div>
                </div>
            </div>
            <div className="flex-shrink-0">
                <input
                    disabled
                    placeholder="Ask about this video…"
                    className="w-full rounded-full border border-gray-600 bg-transparent px-4 py-2 text-sm placeholder:text-gray-500"
                />
            </div>
        </div>
    </div>
);

const MockYoutubeView: React.FC = () => {
  const title = 'The Most DANGEROUS Week In Crypto [Don\'t Make This Mistake]';
  const channel = 'Lab Of Crypto';
  const playerThumb = pickPhotoByTopic(title, channel, 3);
  const avatar = getChannelAvatar(channel);

  return (
    <div className="w-full h-full bg-[#0f0f0f] text-white font-[Roboto,Arial,sans-serif] flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-gray-900/10 transition-shadow duration-300 hover:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.3)]">
      <div className="w-full h-[210px] bg-black flex items-center justify-center overflow-hidden">
        <img src={playerThumb} alt="Video player thumbnail" className="w-full h-full object-cover" loading="lazy" />
      </div>

      <div className="p-3">
        <h2 className="text-md font-bold leading-tight line-clamp-2">{title}</h2>
        <div className="flex items-center mt-3">
          <img className="w-9 h-9 rounded-full" src={avatar} alt="Channel avatar" />
          <div className="ml-3">
            <p className="font-semibold text-sm">{channel}</p>
            <p className="text-xs text-gray-400">170K subscribers</p>
          </div>
        </div>
      </div>

      <div className="mx-3 mt-1 mb-4 p-3 bg-red-500/10 rounded-xl border border-red-500/30 ring-4 ring-red-500/20 animate-pulse">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <TublyLogo className="w-6 h-auto" />
            <h3 className="font-bold text-sm">Tubly Summary</h3>
            </div>
            <div className="flex text-xs bg-black/30 p-1 rounded-lg">
              <button className="px-3 py-1 bg-[#f1f1f1] text-black rounded-md font-semibold">Direct Answer</button>
              <button className="px-3 py-1 text-gray-300">Summary</button>
            </div>
          </div>
        <p className="mt-3 text-xs text-gray-300 leading-relaxed">
          A cautionary breakdown of why this is shaping up to be a high-risk week in crypto—mix of regulatory headlines, leverage unwinds, and thin liquidity. Flags the most common mistake (chasing moves without risk control) and offers practical safeguards like reducing leverage, using self-custody, and waiting for confirmation before entries.
        </p>
      </div>
    </div>
  );  
};

const HowItWorks: React.FC = () => {
  return (
    <div className="bg-white">
      <main>
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900">First, Pin the Extension</h2>
              <p className="mt-3 text-md text-gray-600 max-w-xl mx-auto">
                For easy access, pin the Tubly icon to your browser's toolbar. Click the puzzle icon, find Tubly, and click the pin.
              </p>
            </div>
            <div className="mt-12 max-w-md mx-auto bg-white p-4 rounded-lg shadow-lg border border-gray-200 flex items-center gap-4">
              <PuzzlePieceIcon className="w-8 h-8 text-gray-500 flex-shrink-0" />
              <div className="w-px bg-gray-200 h-10"></div>
              <div className="flex-1 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <TublyLogo className="w-8 h-auto" />
                  <div>
                    <p className="font-semibold text-gray-800">Tubly</p>
                    <p className="text-xs text-gray-500">Your YouTube, Summarized</p>
                  </div>
                </div>
                <PinIcon className="w-7 h-7 text-red-500 cursor-pointer" />
              </div>
            </div>
          </div>
        </section>

        <section className="py-24">
          <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
            <div className="flex justify-center lg:order-2">
              <MockPopupView />
            </div>
            <div className="text-center lg:text-left lg:order-1">
              <p className="font-semibold text-red-500">Step 1</p>
              <h2 className="text-3xl font-bold text-gray-900 mt-2">Check Your Feed</h2>
              <p className="mt-4 text-lg text-gray-600">
                Click the Tubly icon in your toolbar to open your personal dashboard. Here you'll find a feed of the latest videos from your subscribed channels, complete with AI-generated direct answers.
              </p>
            </div>
          </div>
        </section>

        <section className="py-24 bg-gray-50">
          <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
            <div className="flex justify-center">
              <MockVideoDetailView />
            </div>
            <div className="text-center lg:text-left">
              <p className="font-semibold text-red-500">Step 2</p>
              <h2 className="text-3xl font-bold text-gray-900 mt-2">Dive Into the Details</h2>
              <p className="mt-4 text-lg text-gray-600">
                Select any video to view its full summary. You can read the insight or use the clickable key moments to jump straight to the parts that matter most to you.
              </p>
            </div>
          </div>
        </section>

        <section className="py-24">
          <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
            <div className="flex justify-center lg:order-2">
              <div className="w-[450px]">
                <MockYoutubeView />
              </div>
            </div>
            <div className="text-center lg:text-left lg:order-1">
              <p className="font-semibold text-red-500">Step 3</p>
              <h2 className="text-3xl font-bold text-gray-900 mt-2">View Summaries on YouTube</h2>
              <p className="mt-4 text-lg text-gray-600">
                You don't even need to open the extension. Tubly automatically adds a summary block right on the YouTube watch page, below the video player. Direct AI answers are always there when you need them.
              </p>
            </div>
          </div>
        </section>

         <section className="py-24 bg-gray-50">
          <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
            <div className="flex justify-center">
                <MockCopilotView />
            </div>
              <div className="text-center lg:text-left">
                <p className="font-semibold text-red-500">Step 4</p>
                <h2 className="text-3xl font-bold text-gray-900 mt-2">Chat with Videos using Copilot</h2>
                <p className="mt-4 text-lg text-gray-600">
                    Go beyond summaries. Ask specific questions about the video content, and our AI Copilot will find the answer for you, complete with clickable timestamps.
                </p>
            </div>
          </div>
        </section>

        <section className="py-24 bg-gray-800 text-white">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold">Ready to talk to your videos?</h2>
            <p className="mt-3 text-lg text-gray-300">Install Tubly and get summaries, key moments, and Copilot chat on every watch page.</p>
            <div className="mt-8 flex gap-3 justify-center">
              <a href={CHROME_STORE_URL} className="bg-red-500 text-white font-bold py-3 px-8 rounded-lg text-lg transition-transform hover:scale-105 shadow-lg cursor-pointer">
                Add to Chrome
              </a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default HowItWorks;
